import { useSyncExternalStore } from "react"

/**
 * The user's choice to turn off shortcuts that fire on one character key
 * (`/`, `?`, `g h`, `y`, `1`, Shift+E, ...). Shift still types a character,
 * so it does not exempt a shortcut. It is a personal preference like the
 * theme, not organization data, so it is stored once per browser. Shortcuts
 * that need Command, Control or Option, and non-character keys such as
 * Escape, Space and the arrows, stay on.
 */
const SINGLE_KEY_SHORTCUTS_STORAGE_KEY = "coval:single-key-shortcuts-disabled"
const SINGLE_KEY_SHORTCUTS_CHANGE_EVENT = "coval:single-key-shortcuts-change"

// The choice made on this page load when storage refused to save it.
let unsavedEnabled: boolean | null = null

export function areSingleKeyShortcutsEnabled(): boolean {
  if (unsavedEnabled !== null) {
    return unsavedEnabled
  }
  try {
    return (
      window.localStorage.getItem(SINGLE_KEY_SHORTCUTS_STORAGE_KEY) !== "true"
    )
  } catch {
    return true
  }
}

export function setSingleKeyShortcutsEnabled(enabled: boolean) {
  try {
    if (enabled) {
      window.localStorage.removeItem(SINGLE_KEY_SHORTCUTS_STORAGE_KEY)
    } else {
      window.localStorage.setItem(SINGLE_KEY_SHORTCUTS_STORAGE_KEY, "true")
    }
    unsavedEnabled = null
  } catch {
    unsavedEnabled = enabled
    window.addEventListener("storage", forgetUnsavedOnStorageChange)
  }
  window.dispatchEvent(new Event(SINGLE_KEY_SHORTCUTS_CHANGE_EVENT))
}

// Another tab saved or cleared the choice, so storage is the truth again. A
// null key means that tab cleared all of storage.
function forgetUnsavedOnStorageChange(event: StorageEvent): boolean {
  if (event.key !== null && event.key !== SINGLE_KEY_SHORTCUTS_STORAGE_KEY) {
    return false
  }
  unsavedEnabled = null
  return true
}

function subscribe(listener: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (forgetUnsavedOnStorageChange(event)) {
      listener()
    }
  }
  window.addEventListener(SINGLE_KEY_SHORTCUTS_CHANGE_EVENT, listener)
  window.addEventListener("storage", onStorage)
  return () => {
    window.removeEventListener(SINGLE_KEY_SHORTCUTS_CHANGE_EVENT, listener)
    window.removeEventListener("storage", onStorage)
  }
}

export function useSingleKeyShortcutsEnabled(): boolean {
  return useSyncExternalStore(
    subscribe,
    areSingleKeyShortcutsEnabled,
    () => true,
  )
}
